import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  getGetClinicRequestQueryKey,
  getGetClinicStatusForPatientQueryKey,
  useSubmitAnAsyncClinicRequestToCurai,
  useUpdateOngoingCuraiVisitFromAnotherNoriConversation,
} from "@/api/generated";
import { EncounterStartType, type VisitType } from "@/api/generated/models";
import { useGetClinicStatusForPatient } from "@/api/queries/clinicStatus";
import {
  AD_CONVERSION_EVENTS,
  DEPRECATED_ASYNC_REQUEST_SENT_EVENTS,
} from "@/config/adConversionEvents";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import { useSmsConsentNudge } from "@/hooks/useSmsConsentNudge";
import type { SmsConsentNudgeFlow } from "@/store";
import { useActiveProgressionStore } from "@/store/activeProgression";
import { isClinicRequestExpiredError } from "@/utils/clinicRequestError";
import { VISIT_TYPE_BY_ENCOUNTER_TYPE } from "@/utils/visitType";

// live-chat and video-visit are excluded so their nudge can fire later instead.
const SMS_CONSENT_FLOW_BY_ENCOUNTER: Partial<
  Record<EncounterStartType, SmsConsentNudgeFlow>
> = {
  [EncounterStartType.ASYNC_RX]: "async-rx",
  [EncounterStartType.ASYNC_DX]: "async-dx",
  [EncounterStartType.RX_RENEWAL]: "rx-renewal",
};

// Builds the handleComplete that the terminal async_visit step calls to submit the clinic request.
export const useSubmitAsyncClinicRequest = ({
  conversationId,
  clinicRequestId,
  encounterType,
  visitType,
  enabled = true,
}: {
  conversationId?: string;
  clinicRequestId?: string;
  encounterType: EncounterStartType;
  visitType?: VisitType;
  // Gates the clinic-status query; checkout keeps it off until it knows this is an async return.
  enabled?: boolean;
}) => {
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const queryClient = useQueryClient();
  const { triggerNudge: triggerSmsConsentNudge } =
    useSmsConsentNudge(conversationId);
  const {
    data: clinicStatus,
    isLoading: isClinicStatusLoading,
    isFetched: isClinicStatusFetched,
  } = useGetClinicStatusForPatient({ enabled });

  const invalidateClinicQueries = useCallback(() => {
    if (conversationId && clinicRequestId) {
      queryClient.invalidateQueries({
        queryKey: getGetClinicRequestQueryKey(conversationId, clinicRequestId),
      });
    }
    queryClient.invalidateQueries({
      queryKey: getGetClinicStatusForPatientQueryKey(),
    });
  }, [conversationId, clinicRequestId, queryClient]);

  const { mutateAsync: updateCuraiVisit, isPending: isUpdatingCuraiVisit } =
    useUpdateOngoingCuraiVisitFromAnotherNoriConversation({
      mutation: {
        onSuccess: () => {
          track("superclinical-__async-rx-visit-updated", {
            event_context: {
              ...eventContext,
              conversation_id: conversationId,
              clinic_request_id: clinicRequestId,
              encounter_type: encounterType,
            },
          });
          invalidateClinicQueries();
        },
      },
    });
  const {
    mutateAsync: submitAsyncRequestAsync,
    isPending: isSubmitAsyncRequestPending,
  } = useSubmitAnAsyncClinicRequestToCurai({
    mutation: {
      onSuccess: () => {
        const deprecatedSegmentAdEvent =
          DEPRECATED_ASYNC_REQUEST_SENT_EVENTS[
            encounterType as EncounterStartType
          ];
        if (deprecatedSegmentAdEvent) {
          track(deprecatedSegmentAdEvent, {
            event_context: {
              ...eventContext,
              conversation_id: conversationId,
              clinic_request_id: clinicRequestId,
              encounter_type: encounterType,
            },
          });
        }
        const { checkoutPaymentProperties, clearCheckoutPaymentProperties } =
          useActiveProgressionStore.getState();
        track(AD_CONVERSION_EVENTS.visitStarted, {
          ...((clinicRequestId && checkoutPaymentProperties[clinicRequestId]) ||
            {}),
          conversation_id: conversationId,
          clinic_request_id: clinicRequestId,
          encounter_type: encounterType,
          visit_type: visitType ?? VISIT_TYPE_BY_ENCOUNTER_TYPE[encounterType],
          $distinct_id: `visit_started:${clinicRequestId}`,
        });
        if (clinicRequestId) clearCheckoutPaymentProperties(clinicRequestId);
        invalidateClinicQueries();
        const smsConsentFlow =
          SMS_CONSENT_FLOW_BY_ENCOUNTER[encounterType as EncounterStartType];
        if (smsConsentFlow && conversationId) {
          triggerSmsConsentNudge(conversationId, smsConsentFlow);
        }
      },
      onError: (error) => {
        if (isClinicRequestExpiredError(error)) {
          invalidateClinicQueries();
        }
      },
    },
  });

  const handleComplete = useCallback(() => {
    if (!clinicRequestId || !conversationId) return Promise.resolve();

    if (clinicStatus?.hasOngoingClinicVisit) {
      return updateCuraiVisit({
        data: {
          conversation_id: conversationId as string,
          clinic_request_id: clinicRequestId as string,
        },
      });
    }

    return submitAsyncRequestAsync({
      data: {
        origin_conversation_id: conversationId as string,
        clinic_request_id: clinicRequestId as string,
        encounter_type: encounterType as EncounterStartType,
      },
    });
  }, [
    submitAsyncRequestAsync,
    conversationId,
    clinicRequestId,
    updateCuraiVisit,
    clinicStatus?.hasOngoingClinicVisit,
    encounterType,
  ]);

  return {
    handleComplete,
    hasOngoingClinicVisit: clinicStatus?.hasOngoingClinicVisit,
    isClinicStatusLoading,
    isClinicStatusFetched,
    isPending: isSubmitAsyncRequestPending || isUpdatingCuraiVisit,
  };
};
